/// <reference types="youtube" />

import type { YoutubeThumbnailSizeT } from '../../../shared/utils/youtubeThumbnailSize';
import { YoutubeThumbnailSize } from '../../../shared/utils/youtubeThumbnailSize';
import { Data, Effect, Exit, Schema } from 'effect';

/**
 * Singleton YouTube IFrame API loader state
 */
let apiLoadPromise: Promise<void> | null = null;

/**
 * Error types for YouTube operations
 */
export class YouTubeValidationError extends Data.TaggedError('YouTubeValidationError')<{
  readonly input: unknown;
  readonly message: string;
}> {}

export class YouTubeAPILoadError extends Data.TaggedError('YouTubeAPILoadError')<{
  readonly cause: unknown;
  readonly message: string;
}> {}

export class YouTubePlayerInitError extends Data.TaggedError('YouTubePlayerInitError')<{
  readonly videoId: string;
  readonly message: string;
  readonly cause?: unknown;
}> {}

/**
 * Get YouTube thumbnail URL for a given video ID
 * @param path - YouTube video ID
 * @param size - Thumbnail size (default: SMALL)
 * @returns URL to the thumbnail image
 *
 * @example
 * ```typescript
 * getYoutubeThumbnailPath('dQw4w9WgXcQ', YoutubeThumbnailSize.HIGH);
 * // Returns: 'https://img.youtube.com/vi/dQw4w9WgXcQ/hqdefault.jpg'
 * ```
 */
export function getYoutubeThumbnailPath(
  path: string,
  size: YoutubeThumbnailSizeT = YoutubeThumbnailSize.SMALL
): string {
  return `https://img.youtube.com/vi/${path}/${size}.jpg`;
}

/**
 * Get YouTube embed URL for a given video ID
 * @param path - YouTube video ID
 * @returns YouTube embed URL
 *
 * @example
 * ```typescript
 * getYoutubeVideoEmbedFromPath('dQw4w9WgXcQ');
 * // Returns: 'https://www.youtube.com/embed/dQw4w9WgXcQ'
 * ```
 */
export function getYoutubeVideoEmbedFromPath(path: string): string {
  return `https://www.youtube.com/embed/${path}`;
}

/**
 * YouTube video ID schema with validation
 *
 * According to YouTube's documentation, video IDs consist of:
 * - Alphanumeric characters (a-z, A-Z, 0-9)
 * - Underscores (_)
 * - Hyphens (-)
 *
 * Note: YouTube does not officially commit to a specific length.
 * While currently 11 characters, this may change in the future.
 * This schema validates the character set without length constraints.
 *
 * @see https://developers.google.com/youtube/v3/docs/videos
 *
 * @example
 * ```typescript
 * import { YouTubeVideoIdSchema } from '@rcprofi/utils/youtube/validation';
 * import { Schema } from 'effect';
 *
 * const result = Schema.decodeExit(YouTubeVideoIdSchema)('dQw4w9WgXcQ');
 * // result: Either.Right('dQw4w9WgXcQ')
 * ```
 */
export const YouTubeVideoIdSchema = Schema.String.check(
  Schema.isNonEmpty({ message: 'YouTube video ID cannot be empty' }),
  Schema.isPattern(/^[a-zA-Z0-9_-]+$/, {
    message: 'YouTube video ID must contain only alphanumeric characters, underscores, and hyphens',
  })
)
  .pipe(Schema.brand('YouTubeVideoId'))
  .annotate({
    identifier: 'YouTubeVideoId',
    title: 'YouTube Video ID',
    description:
      'A valid YouTube video identifier consisting of alphanumeric characters, underscores, and hyphens',
  });
/**
 * Type representing a validated YouTube video ID
 */
export type YouTubeVideoId = Schema.Schema.Type<typeof YouTubeVideoIdSchema>;

/**
 * Validates if a string is a valid YouTube video ID
 * Uses Effect Schema for validation without throwing exceptions
 *
 * @param id - String to validate as YouTube video ID
 * @returns true if the ID is valid, false otherwise
 *
 * @example
 * ```typescript
 * isValidYouTubeVideoId('dQw4w9WgXcQ'); // true
 * isValidYouTubeVideoId('invalid!@#');   // false (invalid characters)
 * isValidYouTubeVideoId('');             // false (empty string)
 * isValidYouTubeVideoId(null);           // false (not a string)
 * ```
 */
export function isValidYouTubeVideoId(id: unknown): id is YouTubeVideoId {
  const result = Schema.decodeUnknownExit(YouTubeVideoIdSchema)(id);
  return Exit.isSuccess(result);
}

/**
 * Decodes a YouTube video ID using Effect Schema
 * Returns an Effect that succeeds with the validated ID or fails with schema errors
 *
 * @param id - Unknown value to decode as YouTube video ID
 * @returns Effect that resolves to validated YouTubeVideoId or fails with ParseError
 *
 * @example
 * ```typescript
 * import { Effect } from 'effect';
 *
 * const program = Effect.gen(function* () {
 *   const videoId = yield* decodeYouTubeVideoId('dQw4w9WgXcQ');
 *   console.log('Valid video ID:', videoId);
 * });
 * ```
 */
export const decodeYouTubeVideoId = (id: unknown) =>
  Schema.decodeUnknownEffect(YouTubeVideoIdSchema)(id);
/**
 * Decodes a YouTube video ID using Effect Schema with Either result
 * Safe version that returns Either instead of Effect for synchronous validation
 *
 * @param id - Unknown value to decode as YouTube video ID
 * @returns Either.Right with valid ID or Either.Left with error
 *
 * @example
 * ```typescript
 * const result = decodeYouTubeVideoIdEither('dQw4w9WgXcQ');
 * if (Result.isSuccess(result)) {
 *   console.log('Valid ID:', result.right);
 * }
 * ```
 */
export const decodeYouTubeVideoIdEither = (id: unknown) =>
  Schema.decodeUnknownExit(YouTubeVideoIdSchema)(id);

/**
 * Extract YouTube video ID from various URL formats with validation
 * Supports multiple URL formats:
 * - Standard watch: https://www.youtube.com/watch?v=VIDEO_ID
 * - Standard watch with params: https://www.youtube.com/watch?v=VIDEO_ID&other=param
 * - Mobile watch: https://m.youtube.com/watch?v=VIDEO_ID
 * - Embed: https://www.youtube.com/embed/VIDEO_ID
 * - Short URL: https://youtu.be/VIDEO_ID
 * - /v/ format: https://www.youtube.com/v/VIDEO_ID
 * - Already extracted: VIDEO_ID (validated)
 *
 * Note: YouTube video IDs are currently 11 characters, but YouTube doesn't
 * officially commit to this length. This function extracts IDs of any length
 * that match the valid character set.
 *
 * @param url - YouTube URL or video ID to extract from
 * @returns Validated video ID or null if invalid/not found
 *
 * @example
 * ```typescript
 * extractYoutubeVideoId('https://www.youtube.com/watch?v=dQw4w9WgXcQ');
 * // Returns: 'dQw4w9WgXcQ'
 *
 * extractYoutubeVideoId('https://youtu.be/dQw4w9WgXcQ');
 * // Returns: 'dQw4w9WgXcQ'
 *
 * extractYoutubeVideoId('dQw4w9WgXcQ');
 * // Returns: 'dQw4w9WgXcQ'
 *
 * extractYoutubeVideoId('invalid-url');
 * // Returns: null
 * ```
 */
export function extractYoutubeVideoId(url: string): string | null {
  if (!url) return null;

  // Check if it's already a valid video ID
  if (isValidYouTubeVideoId(url)) {
    return url;
  }

  // URL patterns for different YouTube formats
  // Captures any sequence of valid video ID characters (alphanumeric, underscore, hyphen)
  const patterns = [
    // Standard watch with query params (desktop & mobile)
    /(?:youtube\.com|m\.youtube\.com)\/watch\?(?:.*&)?v=([a-zA-Z0-9_-]+)(?:&|$)/,
    // Embed URL (desktop & mobile)
    /(?:youtube\.com|m\.youtube\.com)\/embed\/([a-zA-Z0-9_-]+)(?:[?/]|$)/,
    // Short URL
    /youtu\.be\/([a-zA-Z0-9_-]+)(?:[?/]|$)/,
    // /v/ format (desktop & mobile)
    /(?:youtube\.com|m\.youtube\.com)\/v\/([a-zA-Z0-9_-]+)(?:[?/]|$)/,
  ];

  for (const pattern of patterns) {
    const match = url.match(pattern);
    if (match?.[1] && isValidYouTubeVideoId(match[1])) {
      return match[1];
    }
  }

  return null;
}

/**
 * Configuration for YouTube player initialization
 */
export type YouTubePlayerConfig = {
  readonly videoId: YouTubeVideoId;
  readonly containerId: string;
  readonly width?: string | number;
  readonly height?: string | number;
  readonly autoplay?: boolean;
  readonly mute?: boolean;
  readonly modestbranding?: boolean;
  readonly rel?: number;
  readonly onReady?: (event: YT.PlayerEvent) => void;
};

/**
 * Result of successful player initialization
 */
export type YouTubePlayerResult = {
  readonly player: YT.Player;
  readonly videoId: YouTubeVideoId;
  readonly containerId: string;
};

/**
 * Effect pipeline: Validate video ID
 * Transforms unknown input into validated YouTubeVideoId or fails with validation error
 *
 * @param input - Unknown value to validate as YouTube video ID
 * @returns Effect that resolves to YouTubeVideoId or fails with YouTubeValidationError
 *
 * @example
 * ```typescript
 * const program = pipe(
 *   validateVideoIdEffect('dQw4w9WgXcQ'),
 *   Effect.flatMap(loadAPIEffect),
 *   Effect.tap(videoId => Console.log('Validated:', videoId))
 * );
 * ```
 */
export const validateVideoIdEffect = (input: unknown) =>
  Effect.gen(function* () {
    const result = yield* decodeYouTubeVideoId(input);
    return result;
  }).pipe(
    Effect.mapError((error) => new YouTubeValidationError({ input, message: String(error) }))
  );

/**
 * Loads the YouTube IFrame API script and waits for it to be ready
 * Uses singleton pattern to prevent multiple script loads
 * Internal implementation - use loadAPIEffect() for Effect-based access
 *
 * @returns Promise that resolves when the YouTube API is ready to use
 * @throws Error if called in SSR context or if script fails to load
 */
function loadYouTubeAPI(): Promise<void> {
  // Return existing promise if already loading
  if (apiLoadPromise) {
    return apiLoadPromise;
  }

  // Check SSR context
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('YouTube API can only be loaded in browser environment'));
  }

  // Return immediately if API already loaded
  // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
  if (window.YT?.Player) {
    return Promise.resolve();
  }

  // Create new load promise
  apiLoadPromise = new Promise((resolve, reject) => {
    // Preserve existing callback if any
    const existingCallback = (window as Window & { onYouTubeIframeAPIReady?: () => void })
      .onYouTubeIframeAPIReady;

    // Set up our callback
    (window as Window & { onYouTubeIframeAPIReady?: () => void }).onYouTubeIframeAPIReady = () => {
      // Call existing callback first if it exists
      if (existingCallback) {
        existingCallback();
      }

      // Resolve our promise
      resolve();
    };

    // Check if script is already in the document
    const existingScript = document.querySelector('script[src*="youtube.com/iframe_api"]');

    if (!existingScript) {
      // Create and insert script tag
      const script = document.createElement('script');
      script.src = 'https://www.youtube.com/iframe_api';

      // Handle load errors
      script.addEventListener('error', () => {
        apiLoadPromise = null; // Reset so retry is possible
        reject(new Error('Failed to load YouTube IFrame API script'));
      });

      // Insert into document head
      document.head.appendChild(script);
    }
  });

  return apiLoadPromise;
}

/**
 * Effect pipeline: Load YouTube IFrame API
 * Ensures API is loaded and ready before proceeding
 *
 * @returns Effect that resolves when API is loaded or fails with YouTubeAPILoadError
 *
 * @example
 * ```typescript
 * const program = pipe(
 *   loadAPIEffect(),
 *   Effect.tap(() => Console.log('API loaded successfully'))
 * );
 * ```
 */
export const loadAPIEffect = Effect.tryPromise({
  try: () => loadYouTubeAPI(),
  catch: (error) =>
    new YouTubeAPILoadError({
      cause: error,
      message: error instanceof Error ? error.message : 'Failed to load YouTube API',
    }),
});

/**
 * Effect pipeline: Initialize YouTube Player
 * Creates and configures a YouTube player instance
 *
 * @param config - Player configuration
 * @returns Effect that resolves to YouTubePlayerResult or fails with YouTubePlayerInitError
 *
 * @example
 * ```typescript
 * const program = pipe(
 *   validateVideoIdEffect('dQw4w9WgXcQ'),
 *   Effect.flatMap(videoId =>
 *     initializePlayerEffect({
 *       videoId,
 *       containerId: 'player',
 *       autoplay: true,
 *       mute: true
 *     })
 *   )
 * );
 * ```
 */
export const initializePlayerEffect = (config: YouTubePlayerConfig) =>
  Effect.gen(function* () {
    // Check if YT is available
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (typeof window === 'undefined' || !window.YT?.Player) {
      return yield* new YouTubePlayerInitError({
        videoId: config.videoId,
        message: 'YouTube API not loaded',
      });
    }

    // Create player with error handling
    return yield* Effect.try({
      try: () => {
        const player = new window.YT.Player(config.containerId, {
          videoId: config.videoId,
          width: config.width ?? '100%',
          height: config.height ?? '100%',
          playerVars: {
            autoplay: config.autoplay ? 1 : 0,
            mute: config.mute ? 1 : 0,
            modestbranding: config.modestbranding ? 1 : 0,
            rel: config.rel ?? 0,
          },
          events: {
            onReady: config.onReady,
          },
        });

        return {
          player,
          videoId: config.videoId,
          containerId: config.containerId,
        } satisfies YouTubePlayerResult;
      },
      catch: (error) =>
        new YouTubePlayerInitError({
          videoId: config.videoId,
          message: 'Failed to initialize YouTube player',
          cause: error,
        }),
    });
  });

/**
 * Complete Effect pipeline: Validate → Load API → Initialize Player
 * Composes all steps into a single robust operation
 *
 * @param videoIdInput - Unknown input to validate as video ID
 * @param playerConfig - Partial player configuration (videoId will be added)
 * @returns Effect that resolves to YouTubePlayerResult with all error types in union
 *
 * @example
 * ```typescript
 * const program = createYouTubePlayerEffect('dQw4w9WgXcQ', {
 *   containerId: 'player',
 *   autoplay: true,
 *   mute: true
 * }).pipe(
 *   Effect.tap(result => Console.log('Player ready:', result)),
 *   Effect.catchTags({
 *     YouTubeValidationError: error => Console.error('Invalid video ID:', error),
 *     YouTubeAPILoadError: error => Console.error('API load failed:', error),
 *     YouTubePlayerInitError: error => Console.error('Player init failed:', error)
 *   })
 * );
 * ```
 */
export const createYouTubePlayerEffect = (
  videoIdInput: unknown,
  playerConfig: Omit<YouTubePlayerConfig, 'videoId'>
) =>
  Effect.gen(function* () {
    // Step 1: Validate video ID
    const videoId = yield* validateVideoIdEffect(videoIdInput);

    // Step 2: Load YouTube API
    yield* loadAPIEffect;

    // Step 3: Initialize player
    const result = yield* initializePlayerEffect({
      ...playerConfig,
      videoId,
    });

    return result;
  });

/**
 * Safe player cleanup effect
 * Ensures player is properly destroyed and resources are released
 *
 * @param player - YouTube player instance to clean up
 * @returns Effect that always succeeds (errors are logged but not thrown)
 *
 * @example
 * ```typescript
 * Effect.gen(function* () {
 *   const result = yield* createYouTubePlayerEffect(...);
 *
 *   // Use player...
 *
 *   yield* cleanupPlayerEffect(result.player);
 * });
 * ```
 */
export const cleanupPlayerEffect = (player: YT.Player | null) =>
  Effect.try({
    try: () => {
      if (player) {
        player.destroy();
      }
    },
    catch: () => {
      /* Ignore cleanup errors */
    },
  }).pipe(Effect.orElseSucceed(() => undefined));

/**
 * Effect pipeline with automatic cleanup
 * Ensures player is cleaned up even if an error occurs
 *
 * @param videoIdInput - Unknown input to validate as video ID
 * @param playerConfig - Partial player configuration
 * @param onPlayer - Function that uses the player and returns an Effect
 * @returns Effect that resolves to the result of onPlayer callback
 *
 * @example
 * ```typescript
 * const program = withYouTubePlayer('dQw4w9WgXcQ', {
 *   containerId: 'player',
 *   autoplay: true
 * }, (result) =>
 *   Effect.gen(function* () {
 *     yield* Effect.sleep('5 seconds');
 *     result.player.pauseVideo();
 *     return 'Video paused after 5 seconds';
 *   })
 * );
 * ```
 */
export const withYouTubePlayer = <A, E, R>(
  videoIdInput: unknown,
  playerConfig: Omit<YouTubePlayerConfig, 'videoId'>,
  onPlayer: (result: YouTubePlayerResult) => Effect.Effect<A, E, R>
) =>
  Effect.gen(function* () {
    const result = yield* createYouTubePlayerEffect(videoIdInput, playerConfig);

    return yield* Effect.ensuring(onPlayer(result), cleanupPlayerEffect(result.player));
  });
